import {
  PortfolioClosedDealResource,
  PortfolioCustomerResource,
  PortfolioUserResource,
  PortfolioVehicleResource,
} from '@appTypes/PortfolioResource';
import LastUpdated from '@components/shared/react-table-wrapper/columns/last-updated/LastUpdated';
import Tooltip from '@components/shared/tooltip/Tooltip';
import { Typography } from '@components/style/Typography';
import { useCheckDevice } from '@hooks/UseCheckDevice';
import { useDealStatus } from '@hooks/UseDealStatus';
import { useDealType } from '@hooks/UseDealType';
import { BrandsService } from '@services/shared/BrandsService';
import { theme } from '@theme';
import { FC } from 'react';
import { Link } from 'react-router';

type BuyerCurrentVehicleProps = {
  customer: PortfolioCustomerResource;
  currentVehicle: PortfolioVehicleResource;
  closedDeal: PortfolioClosedDealResource;
  dealerId: string;
  vinNumber: string;
  user: PortfolioUserResource;
};

const BuyerCurrentVehicle: FC<BuyerCurrentVehicleProps> = ({
  customer,
  currentVehicle,
  closedDeal,
  dealerId,
  vinNumber,
  user,
}) => {
  const { getColor } = useDealStatus();

  const { getDealTypeLabel } = useDealType();

  const { isMobileDevice } = useCheckDevice();

  const getSocialAvatar = (): string => {
    const baseUrl = 'https://storage.googleapis.com/customer-images-prd/socialmedia/';
    return customer?.social_avatar
      ? baseUrl.concat(dealerId?.toLowerCase(), '/', customer?.social_avatar)
      : baseUrl.concat('avatar-incognito.svg');
  };

  const getDealStatusCssClass = (status: string): string => {
    return status?.replace(/ /g, '').toLowerCase();
  };

  const getMakeName = () => {
    const abbreviation = dealerId?.slice(0, 2);
    if (abbreviation === 'OB') {
      return BrandsService.getByBrandId(currentVehicle.brandId);
    }
    return BrandsService.getByDealerId(dealerId);
  };

  let dealsheetUrl;
  //TODO: remove this once once we dashborad mobile redesign is complete
  const getDeviceType = () => {
    const windowWidth = window.innerWidth;
    if (windowWidth > 0 && windowWidth <= 752) {
      return 'mobile';
    } else {
      return 'desktop';
    }
  };
  const device = getDeviceType();

  if (device === 'mobile') {
    dealsheetUrl = `/dealsheet/${vinNumber}/${dealerId}/offer/1/${getDealTypeLabel(closedDeal.dealType).toLowerCase()}/m`;
  } else {
    dealsheetUrl = `/dealsheet/${vinNumber}/${dealerId}/offer/1/${getDealTypeLabel(closedDeal.dealType).toLowerCase()}/bps`;
  }

  return (
    <div className='buyer-vehicle-container' data-testid='buyer-vehicle-container'>
      {!isMobileDevice && (
        <div className='buyer-avatar'>
          <img
            src={getSocialAvatar()}
            alt='Default Avatar'
            className={`avatar-portfolio-dashboard ${getDealStatusCssClass(closedDeal?.statusDisplayName)}`}
            data-testid='buyer-avatar'
          />
        </div>
      )}
      <div className='buyer-vehicle-details'>
        <Link to={dealsheetUrl}>
          <Typography
            size={theme.typography.h4}
            fontWeight={theme.typography.fontWeights.bold}
            data-testid='buyer-details'
          >
            {customer?.fullName}
          </Typography>
        </Link>
        <br />
        {!closedDeal?.isTrimConfirmationRequired ? (
          <>
            <Typography
              size={theme.typography.h5}
              fontWeight={theme.typography.fontWeights.bold}
              data-testid='vehicle-year-make'
            >
              {currentVehicle?.year} {getMakeName()}
            </Typography>
            &nbsp;
            <Typography
              size={theme.typography.h5}
              fontWeight={theme.typography.fontWeights.normal}
              data-testid='vehicle-trim'
            >
              {currentVehicle?.trim}
            </Typography>
            <br />
          </>
        ) : (
          <>
            <Typography
              size={theme.typography.h5}
              fontWeight={theme.typography.fontWeights.normal}
              data-testid='vehicle-trim'
            >
              {getMakeName()} Trim Unconfirmed
            </Typography>
            <br />
          </>
        )}
        {closedDeal?.subStatusDisplayName !== '' &&
        closedDeal?.subStatusDisplayName !== null &&
        !isMobileDevice ? (
          <Tooltip
            trigger={
              <Typography
                size={theme.typography.h5}
                fontWeight={theme.typography.fontWeights.bold}
                color={
                  closedDeal?.statusDisplayName !== null && closedDeal?.statusDisplayName !== ''
                    ? getColor(closedDeal?.statusDisplayName)
                    : theme.palette.purple
                }
                data-testid='deal-status'
              >
                {closedDeal?.statusDisplayName}
              </Typography>
            }
            content={
              <Typography size={theme.typography.h5} color={theme.palette.lightGray}>
                {closedDeal?.subStatusDisplayName}
              </Typography>
            }
            cssClass='portfolio-tooltip'
          />
        ) : (
          <Typography
            size={theme.typography.h5}
            fontWeight={theme.typography.fontWeights.bold}
            color={
              closedDeal?.statusDisplayName !== null && closedDeal?.statusDisplayName !== ''
                ? getColor(closedDeal?.statusDisplayName)
                : theme.palette.purple
            }
            data-testid='deal-status'
          >
            {closedDeal?.statusDisplayName}
          </Typography>
        )}
        {isMobileDevice && (
          <>
            <br />
            <b>Assignee:</b>{' '}
            <Typography size={theme.typography.h5} fontWeight={theme.typography.fontWeights.normal}>
              {user?.fullName ?? 'NA'}
            </Typography>{' '}
            <b>• Updated:</b>{' '}
            {closedDeal?.sinceUpdatedDate !== null && closedDeal?.sinceUpdatedDate !== undefined ? (
              <LastUpdated lastUpdatedText=' D' lastUpdated={closedDeal?.sinceUpdatedDate} />
            ) : (
              'NA'
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BuyerCurrentVehicle;
